import React, { Component, useCallback, useEffect, useMemo } from 'react';
import OriginTracing from '@atlassiansox/origin-tracing';
import { ShareDialogContainer, type Flag, type ShareDialogContainerProps } from '@atlaskit/share';
import type { OptionData } from '@atlaskit/user-picker';
import type { ShareServiceClient } from '@atlaskit/share/clients';
import { getTenantContext_DEPRECATED_DO_NOT_USE } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { useFlagsService, withFlagService, type FlagService } from '@atlassian/jira-flags';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { defineMessages, useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { LegacyShareClientConfig } from '../../common/types.tsx';
import { createAri } from '../../common/utils/ari.tsx';
import LegacyShareClient from '../../services/legacy-share-client/index.tsx';
import { loadUsers } from '../../services/load-users/index.tsx';
import { UserRole } from '../user-role/index.tsx';
import type { ShareDialogFormData } from '../types.tsx';
import { useMaybeCanSeeExtendedShareDialog } from '../../common/hooks/use-maybe-can-see-extended-share-dialog.tsx';
import { useProjectAccessLevel } from '../../services/use-project-access-level/index.tsx';
import { CustomShareSubmitButton } from '../custom-share-submit/index.tsx';
import type { UserRoleType } from '../../services/use-user-role/types.tsx';
import { useUserRole } from '../../services/use-user-role/index.tsx';
import type { AccessLevel } from '../../services/use-project-access-level/types.tsx';
import { ANALYTICS_SOURCE } from '../../constants.tsx';
import type { Props, UserData, ServerUsersAndGroups } from './types.tsx';

export type ButtonProps = Props & {
	flagService: FlagService;
	onTriggerButtonClick?: () => void;
	shouldAutoDismissFlag?: boolean;
	legacyShareClientConfig?: LegacyShareClientConfig;
	orgId?: string;
	isBrowseUsersEnabled?: boolean;
	shareFormHelperMessage?: string;
	shareFieldsFooter?: React.ReactNode;
	workspaceAri?: string;
	customHeader?: React.ReactNode;
	userRoles?: UserRoleType[];
	userRolesLoading?: boolean;
	accessLevel?: AccessLevel;
	maybeExtendedShareDialogEnabled?: boolean;
} & Partial<Intl> &
	Pick<ShareDialogContainerProps, 'onSubmit'>;

const USER_PICKER_LIMIT = 20;

// eslint-disable-next-line jira/react/no-class-components
export class ShareButton extends Component<ButtonProps> {
	static defaultProps = {
		shouldAutoDismissFlag: true,
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onTriggerButtonClick: () => {},
	};

	constructor(props: ButtonProps) {
		super(props);
		this.cloudId = getTenantContext_DEPRECATED_DO_NOT_USE().cloudId;

		if (props.legacyShareClientConfig !== undefined) {
			this.legacyShareClient = new LegacyShareClient(props.legacyShareClientConfig);
		}
	}

	legacyShareClient: undefined | ShareServiceClient;

	getOriginTracingInstance = (): OriginTracing =>
		new OriginTracing({ product: this.props.productId });

	loadUsers = async (searchText = ''): Promise<OptionData[]> => {
		const searchTextEncoded = encodeURIComponent(searchText);
		const url = `/rest/api/3/groupuserpicker?maxResults=${USER_PICKER_LIMIT}&query=${searchTextEncoded}&showAvatar=true&avatarSize=medium&excludeConnectAddons=true`;
		const groupsAndUsers: ServerUsersAndGroups = await fetchJson(url);
		return groupsAndUsers.users.users
			.filter((user) => user.accountType === 'atlassian')
			.map((user) => {
				const userData: UserData = {
					type: 'user',
					id: user.accountId,
					name: user.displayName,
					avatarUrl: user.avatarUrl,
				};
				return userData;
			});
	};

	showFlags = (flags: Flag[]) => {
		const { flagService, shouldAutoDismissFlag } = this.props;
		if (flagService?.showFlag) {
			flags.forEach((flag: Flag) => {
				flagService.showFlag({
					type: flag.appearance,
					title: flag.title.defaultMessage,
					isAutoDismiss: shouldAutoDismissFlag,
					messageId: `share-button.ui.share-view.showFlag.${flag.title.id}`,
					messageType: 'transactional',
				});
			});
		}
	};

	cloudId = '';

	render() {
		const {
			objectAri,
			renderCustomTriggerButton,
			shareLink,
			shareTitle,
			shareContentType,
			shareFormTitle,
			subProductId,
			triggerButtonAppearance = 'subtle',
			triggerButtonStyle,
			onTriggerButtonClick,
			shareIntegrations,
			integrationMode = 'off',
			isAutoOpenDialog,
			onDialogClose,
			copyTooltipText,
			orgId,
			isBrowseUsersEnabled,
			shareFormHelperMessage,
			shareFieldsFooter,
			additionalTabs,
			builtInTabContentWidth,
			isOutgoingMailDisabled,
			customHeader,
			maybeExtendedShareDialogEnabled = false,
			onSubmit,
			userRoles = [],
			userRolesLoading,
		} = this.props;

		const isExtendedShareDialogEnabled =
			maybeExtendedShareDialogEnabled &&
			expVal('tmp_secure_project_access_share_dialog', 'isEnabled', false);

		return (
			<ShareDialogContainer
				shareClient={this.legacyShareClient}
				triggerButtonAppearance={triggerButtonAppearance}
				triggerButtonStyle={triggerButtonStyle}
				cloudId={this.cloudId}
				orgId={orgId}
				loadUserOptions={this.legacyShareClient && this.loadUsers}
				// @ts-expect-error - TS2322 - Type '() => OriginTracing' is not assignable to type 'OriginTracingFactory'.
				originTracingFactory={this.getOriginTracingInstance}
				productId={subProductId}
				renderCustomTriggerButton={renderCustomTriggerButton}
				shareAri={objectAri || createAri(subProductId, this.cloudId)}
				shareFormHelperMessage={shareFormHelperMessage}
				shareLink={shareLink}
				shareTitle={shareTitle}
				shareContentType={shareContentType}
				shareFormTitle={shareFormTitle}
				showFlags={this.showFlags}
				enableSmartUserPicker={!this.legacyShareClient}
				product="jira"
				onTriggerButtonClick={onTriggerButtonClick}
				shareIntegrations={shareIntegrations}
				integrationMode={integrationMode}
				isAutoOpenDialog={isAutoOpenDialog}
				onDialogClose={onDialogClose}
				copyTooltipText={copyTooltipText}
				isBrowseUsersDisabled={isBrowseUsersEnabled === false}
				shareFieldsFooter={shareFieldsFooter}
				workspaceAri={this.props.workspaceAri}
				additionalTabs={additionalTabs}
				builtInTabContentWidth={builtInTabContentWidth}
				isSubmitShareDisabled={isOutgoingMailDisabled}
				customHeader={customHeader}
				isExtendedShareDialogEnabled={isExtendedShareDialogEnabled}
				additionalUserFields={
					isExtendedShareDialogEnabled && (
						<UserRole roles={userRoles} isLoading={!!userRolesLoading} />
					)
				}
				CustomSubmitButton={isExtendedShareDialogEnabled ? CustomShareSubmitButton : undefined}
				onSubmit={onSubmit}
			/>
		);
	}
}

export const messages = defineMessages({
	projectAccessLevelUpdated: {
		id: 'share-button.ui.share-view.project-access-level-updated',
		defaultMessage: 'Access updated',
		description: 'Label for the project access level update event',
	},
	errorUpdateProjectAccessLevel: {
		id: 'share-button.ui.share-view.error-update-project-access-level',
		defaultMessage: 'Error updating project access level',
		description: 'Error message shown when there is an issue updating the project access level',
	},
	errorAddingUsersToRole: {
		id: 'share-button.ui.share-view.error-adding-users-to-role',
		defaultMessage: 'Error adding users to role',
		description: 'Error message shown when there is an issue adding users to a role',
	},
});

const noop = () => null;

const ShareButtonFC = ({
	objectAri,
	triggerButtonAppearance = 'subtle',
	onTriggerButtonClick = noop,
	integrationMode = 'off',
	isBrowseUsersEnabled,
	isOutgoingMailDisabled,
	legacyShareClientConfig,
	shouldAutoDismissFlag = false,
	onSubmit,
	userRoles = [],
	userRolesLoading,
	accessLevel,
	...props
}: Omit<ButtonProps, 'flagService'>) => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const maybeExtendedShareDialogEnabled = useMaybeCanSeeExtendedShareDialog();
	const isExtendedShareDialogEnabled =
		maybeExtendedShareDialogEnabled &&
		expVal('tmp_secure_project_access_share_dialog', 'isEnabled', false);

	const legacyShareClient = useMemo(
		() => legacyShareClientConfig && new LegacyShareClient(legacyShareClientConfig),
		[legacyShareClientConfig],
	);

	const getOriginTracingInstance = useCallback((): OriginTracing => {
		return new OriginTracing({ product: 'jira' });
	}, []);

	const showFlags = useCallback(
		(flags: Flag[]) => {
			if (showFlag) {
				flags.forEach((flag: Flag) => {
					showFlag({
						type: flag.appearance,
						title: flag.title.defaultMessage,
						isAutoDismiss: shouldAutoDismissFlag,
						messageId: `share-button.ui.share-view.showFlag.${flag.title.id}`,
						messageType: 'transactional',
					});
				});
			}
		},
		[showFlag, shouldAutoDismissFlag],
	);

	const { data: projectContext } = useProjectContext();
	const { update: updateProjectAccessLevel, updateError: projectAccessLevelError } =
		useProjectAccessLevel();
	const { add: addToUserRole, updateError: useRoleError } = useUserRole(projectContext?.projectKey);

	useEffect(() => {
		if (projectAccessLevelError) {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'jiraUsersAndRoles.updateAccessType failed',
				'changeAccessButton',
				{
					source: ANALYTICS_SOURCE,
					projectType: projectContext?.projectType,
					isTmp: true,
					error: projectAccessLevelError,
				},
			);

			showFlag({
				type: 'error',
				title: formatMessage(messages.errorUpdateProjectAccessLevel),
				isAutoDismiss: shouldAutoDismissFlag,
				messageId: 'share-button.ui.share-view.show-flag.error.update-project-access-level',
				messageType: 'transactional',
			});
		}
	}, [
		projectAccessLevelError,
		formatMessage,
		showFlag,
		shouldAutoDismissFlag,
		createAnalyticsEvent,
		projectContext?.projectType,
	]);

	useEffect(() => {
		if (useRoleError) {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'jiraUsersAndRoles.addActorToRole failed',
				'roleOption',
				{
					source: ANALYTICS_SOURCE,
					packageName: '@atlassian/jira-share-button',
					error: useRoleError,
				},
			);

			showFlag({
				type: 'error',
				title: formatMessage(messages.errorAddingUsersToRole),
				isAutoDismiss: shouldAutoDismissFlag,
				messageId: 'share-button.ui.share-view.show-flag.error.adding-users-to-role.1',
				messageType: 'transactional',
			});
		}
	}, [useRoleError, formatMessage, showFlag, shouldAutoDismissFlag, createAnalyticsEvent]);

	const handleOnSubmit = useCallback(
		(data: ShareDialogFormData) => {
			if (isExtendedShareDialogEnabled) {
				if (data?.projectAccessLevel?.value && data?.projectAccessLevel?.value !== accessLevel) {
					// We use `.then()` syntax here instead of changing the entire function to `async` to avoid blocking the user role updates below
					updateProjectAccessLevel({
						accessLevel: data?.projectAccessLevel.value,
					}).then((response) => {
						if (!response) {
							return;
						}

						fireTrackAnalytics(createAnalyticsEvent({}), 'project updated', 'changeAccessButton', {
							source: ANALYTICS_SOURCE,
							projectType: projectContext?.projectType,
							isTmp: true,
							updatedItem: 'projectAccess',
							accessLevel: data?.projectAccessLevel?.value,
						});

						if (!data.users?.length) {
							showFlag({
								type: 'success',
								title: formatMessage(messages.projectAccessLevelUpdated),
								isAutoDismiss: shouldAutoDismissFlag,
								messageId:
									'share-button.ui.share-view.show-flag.success.project-access-level-updated',
								messageType: 'transactional',
							});
						}
					});
				}

				if (data.users?.length > 0 && data?.userRole?.value) {
					const { value: roleId, label: roleName } = data.userRole;
					const userIds: string[] = data.users
						.filter((user: UserData) => user.type === 'user')
						.map((user: UserData) => user.id);

					const groupIds: string[] = data.users
						.filter((user: UserData) => user.type === 'group')
						.map((group: UserData) => group.id);

					addToUserRole(roleId, {
						users: userIds,
						groups: groupIds,
					}).then((response) => {
						if (!response) {
							return;
						}
						fireTrackAnalytics(createAnalyticsEvent({}), 'project updated', 'roleOption', {
							source: ANALYTICS_SOURCE,
							roleId,
							roleName,
							numberOfExistingUsersAdded: userIds.length,
							numberOfExistingGroupsAdded: groupIds.length,
							updatedItem: 'projectRole',
						});
					});
				}
			}

			onSubmit && onSubmit(data);
		},
		[
			isExtendedShareDialogEnabled,
			onSubmit,
			accessLevel,
			updateProjectAccessLevel,
			createAnalyticsEvent,
			projectContext?.projectType,
			showFlag,
			formatMessage,
			shouldAutoDismissFlag,
			addToUserRole,
		],
	);

	return (
		<ShareDialogContainer
			shareClient={legacyShareClient}
			cloudId={cloudId}
			loadUserOptions={legacyShareClient && loadUsers}
			// @ts-expect-error - TS2322 - Type '() => OriginTracing' is not assignable to type 'OriginTracingFactory'.
			originTracingFactory={getOriginTracingInstance}
			shareAri={objectAri || createAri(props.subProductId, cloudId)}
			showFlags={showFlags}
			enableSmartUserPicker={!legacyShareClient}
			product="jira"
			isBrowseUsersDisabled={isBrowseUsersEnabled === false}
			isSubmitShareDisabled={isOutgoingMailDisabled}
			triggerButtonAppearance={triggerButtonAppearance}
			onTriggerButtonClick={onTriggerButtonClick}
			integrationMode={integrationMode}
			isExtendedShareDialogEnabled={isExtendedShareDialogEnabled}
			onSubmit={handleOnSubmit}
			CustomSubmitButton={isExtendedShareDialogEnabled ? CustomShareSubmitButton : undefined}
			additionalUserFields={
				isExtendedShareDialogEnabled && (
					<UserRole accessLevel={accessLevel} roles={userRoles} isLoading={!!userRolesLoading} />
				)
			}
			{...props}
		/>
	);
};

export default componentWithCondition(
	() => fg('jira-growth-share-button-refactor-fc'),
	ShareButtonFC,
	injectIntl(withFlagService(ShareButton)),
);
